import { viewPane$ } from './viewPane$';
import {
  distinctUntilChanged,
  map,
  scan,
  shareReplay,
  tap,
} from 'rxjs/operators';
import { combineLatest, filter } from 'rxjs';
import { dispatchAction } from '@ardoq/rxbeach';
import { fetchDashboardData } from '../../dashboard/DashboardBuilder/streams/actions';
import { trackEvent } from '../../tracking/tracking';
import {
  DashboardEventLocations,
  DashboardFilterReaderIslandRenderProps,
  DashboardModes,
  DashboardTrackingEventsNames,
  FullscreenWidgetData,
  getDashboardColors,
  getDashboardFilterReaderIslandProps,
  getFullscreenWidgetModalProps,
  LoadedDashboard,
  OnWidgetDrilldown,
} from '@ardoq/dashboard';
import { loadedDashboards$ } from '../../dashboard/DashboardBuilder/loadedDashboards$';
import { METRICS_DSB_ID } from '../../dashboard/consts';
import { getReportInDiscoverURL } from '@ardoq/report-reader';
import {
  APIDashboardFilterExpression,
  APISlideAttributes,
  ArdoqId,
  DashboardFilterValues,
  DashboardSlideParams,
  isDashboardSlide,
  SupportedDashboardFilterType,
} from '@ardoq/api-types';
import { loadedPresentation$ } from 'presentation/streams/loadedPresentation$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { getWidgetForDashboardSlide } from './utils';
import { track, noTracking, TrackFn } from 'tracking/track';
import type { DashboardSlideProps } from './DashboardSlide';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import {
  closedFullscreenWidget,
  fullscreenWidget$,
  openedFullscreenWidget,
} from '../../dashboard/DashboardReader/fullscreenWidget$';
import { isEqual } from 'lodash';
import { getCopyDashboardUrlButtonProps } from '../../dashboard/DashboardReader/commands/copyUrlButtonCommands';
import { featureContext$ } from '../../streams/featureContext$';
import { featureOperations, Features } from '@ardoq/features';
import { loadedDashboardsOperations } from 'dashboard/DashboardBuilder/loadedDashboardsOperations';
import {
  DashboardFilter$State,
  getDashboardFilter$,
} from 'dashboard/DashboardBuilder/streams/dashboardFilter$';
import {
  DashboardFilterPillsUI$State,
  dashboardFilterPillsUI$,
} from 'dashboard/DashboardBuilder/streams/dashboardFilterPillsUI$';
import { toDashboardFilterPillsUIStateWithSetters } from 'dashboard/DashboardBuilder/streams/stateUtils';
import { getDashboardPillLabels } from 'tableFilterBar/getTableFilterBarLabels';
import {
  filterCachedHierarchicalSuggestions,
  PresentationSuggestionsContext,
  requestDashboardFilterHierarchicalSuggestions,
  requestDashboardFilterSuggestions,
} from 'dashboard/dashboardFiltersSuggestions$';
import { LoadingState } from 'dashboard/DashboardBuilder/types';
import {
  presentationDashboardFilterChanged,
  presentationDashboardFilters$,
  presentationDashboardFiltersCleared,
} from './presentationDashboardFilters$';
import {
  getEffectiveFilterValues,
  getRequestFilterValues,
  isSlideFilterLocked,
} from './presentationDashboardFilterOperations';
import { isTruthy, Maybe } from '@ardoq/common-helpers';
import { Locale } from '@ardoq/locale';
import { I18nLocale } from '@ardoq/i18n';
import { isPptxExportRunning$ } from 'presentation/pptxExport/pptxExport$';

const toDashboardSlideProps = (
  slide: APISlideAttributes | null
):
  | {
      dashboardId: ArdoqId;
      slideId: ArdoqId;
      dashboardParams: DashboardSlideParams | null;
    }
  | undefined =>
  slide && isDashboardSlide(slide)
    ? {
        dashboardId: slide.dashboardId,
        slideId: slide._id,
        dashboardParams: slide.dashboardParams,
      }
    : undefined;

const onWidgetDrilldown: OnWidgetDrilldown = ({ datasource, filters }) => {
  window.open(
    getReportInDiscoverURL(datasource.sourceId, filters, 'presentation'),
    '_blank'
  );
};

type DashboardSlideFetchContext = {
  dashboardId: ArdoqId;
  slideId: ArdoqId;
  dashboardParams: DashboardSlideParams | null;
  presentationId: ArdoqId | null;
  effectiveFilterValues: DashboardFilterValues | undefined;
  requestFilterValues: DashboardFilterValues | undefined;
};

type DashboardSlideRenderContext = DashboardSlideFetchContext & {
  isFilterChangeOnCurrentSlide: boolean;
};

const toRenderContext = (
  previousContext: DashboardSlideRenderContext | null,
  context: DashboardSlideFetchContext
): DashboardSlideRenderContext => ({
  ...context,
  isFilterChangeOnCurrentSlide:
    previousContext !== null &&
    previousContext.dashboardId === context.dashboardId &&
    previousContext.slideId === context.slideId,
});

const dashboardSlideFetchContext$ = combineLatest([
  viewPane$.pipe(
    map(({ loadedSlide }) => loadedSlide),
    map(toDashboardSlideProps),
    filter(Boolean)
  ),
  loadedPresentation$,
  presentationDashboardFilters$,
]).pipe(
  map(
    ([
      { dashboardId, slideId, dashboardParams },
      { loadedPresentationId },
      viewerFilterValuesByDashboardId,
    ]): DashboardSlideFetchContext => ({
      dashboardId,
      slideId,
      dashboardParams,
      presentationId: loadedPresentationId,
      effectiveFilterValues: getEffectiveFilterValues(
        dashboardParams,
        viewerFilterValuesByDashboardId[dashboardId]
      ),
      requestFilterValues: getRequestFilterValues(
        dashboardParams,
        viewerFilterValuesByDashboardId[dashboardId]
      ),
    })
  ),
  distinctUntilChanged(isEqual),
  tap(({ dashboardId, slideId, presentationId, requestFilterValues }) => {
    if (presentationId) {
      dispatchAction(
        fetchDashboardData({
          dashboardId,
          presentationId,
          slideId,
          globalFilterValues: requestFilterValues,
          allowStaleWhileRevalidate: false,
        })
      );
    }
  }),
  scan<DashboardSlideFetchContext, DashboardSlideRenderContext | null>(
    toRenderContext,
    null
  ),
  filter(isTruthy),
  // `dashboardSlide$` subscribes this twice (directly and through
  // `dashboardSlideFilterSource$`); sharing keeps the fetch dispatched once.
  shareReplay({ bufferSize: 1, refCount: true })
);

const dashboardSlideFilterSource$ = combineLatest([
  dashboardSlideFetchContext$,
  loadedDashboards$,
]).pipe(
  map(([{ dashboardId }, loadedDashboardsState]) => {
    const dashboardData = loadedDashboardsOperations.getLoadedDashboardData(
      loadedDashboardsState,
      dashboardId
    );

    return {
      widgets: dashboardData?.widgets ?? [],
      globalFilterParameters: dashboardData?.globalFilterParameters,
      globalFilterValues:
        loadedDashboardsState.apiLoadingGlobalFilterValues ??
        dashboardData?.globalFilterValues ??
        {},
    };
  })
);

const closeFullscreenWidget = () => dispatchAction(closedFullscreenWidget());

const openFullscreenWidget = (widgetData: FullscreenWidgetData) => {
  trackEvent(DashboardTrackingEventsNames.OPENED_FULLSCREEN_WIDGET, {
    from: DashboardEventLocations.PRESENTATION,
    widgetType: widgetData.viewType,
  });
  dispatchAction(openedFullscreenWidget({ widgetData }));
};

type GetSlideDashboardDataArgs = {
  loadedDashboardData: Maybe<LoadedDashboard>;
  effectiveFilterValues: DashboardFilterValues | undefined;
  isFilterChangeOnCurrentSlide: boolean;
};

/**
 * Cached data for another slice of the same dashboard must not be shown as this
 * slide's data, but a viewer changing a filter on the slide they are already on
 * keeps the slice they came from until the new one arrives — dropping it would
 * unmount the filter island they are interacting with.
 */
const getSlideDashboardData = ({
  loadedDashboardData,
  effectiveFilterValues,
  isFilterChangeOnCurrentSlide,
}: GetSlideDashboardDataArgs): Maybe<LoadedDashboard> => {
  if (!loadedDashboardData) return null;
  if (isFilterChangeOnCurrentSlide) return loadedDashboardData;
  return isEqual(
    effectiveFilterValues ?? {},
    loadedDashboardData.globalFilterValues ?? {}
  )
    ? loadedDashboardData
    : null;
};

type GetSlideFilterIslandPropsArgs = {
  dashboardId: ArdoqId;
  presentationContext: PresentationSuggestionsContext | null;
  dashboardFilters: DashboardFilter$State['dashboardFilters'];
  dashboardFilterPillsUIState: DashboardFilterPillsUI$State;
  isLoading: boolean;
  userLocale: Locale;
  i18nLocale: I18nLocale;
  trackFilterEvent: TrackFn;
};

const getSlideFilterIslandProps = ({
  dashboardId,
  presentationContext,
  dashboardFilters,
  dashboardFilterPillsUIState,
  isLoading,
  userLocale,
  i18nLocale,
  trackFilterEvent,
}: GetSlideFilterIslandPropsArgs): DashboardFilterReaderIslandRenderProps => {
  const updateDashboardFilterValues = (
    dashboardFilterId: string,
    type: SupportedDashboardFilterType,
    values: APIDashboardFilterExpression[] | null
  ) => {
    trackFilterEvent(
      DashboardTrackingEventsNames.UPDATED_DASHBOARD_FILTER_VALUES,
      { from: DashboardEventLocations.PRESENTATION, type }
    );
    dispatchAction(
      presentationDashboardFilterChanged({
        dashboardId,
        dashboardFilterId,
        values,
      })
    );
  };

  const clearAllSelectedValues = () =>
    dispatchAction(presentationDashboardFiltersCleared({ dashboardId }));

  const requestSuggestions = (parameterId: ArdoqId, query: string) =>
    dispatchAction(
      requestDashboardFilterSuggestions({
        dashboardId,
        parameterId,
        query,
        dashboardFilters,
        isInDashboardBuilder: false,
        presentationContext,
      })
    );

  const requestHierarchicalSuggestions = (
    parameterId: ArdoqId,
    query: string
  ) =>
    dispatchAction(
      requestDashboardFilterHierarchicalSuggestions({
        dashboardId,
        parameterId,
        query,
        dashboardFilters,
        isInDashboardBuilder: false,
        presentationContext,
      })
    );

  const filterCachedSuggestions = (parameterId: ArdoqId, query: string) =>
    dispatchAction(filterCachedHierarchicalSuggestions({ parameterId, query }));

  const filterPillsUIStateWithSetters = Object.fromEntries(
    dashboardFilters.map(dashboardFilter => [
      dashboardFilter.id,
      toDashboardFilterPillsUIStateWithSetters(
        dashboardFilter.id,
        dashboardFilterPillsUIState
      ),
    ])
  );

  return getDashboardFilterReaderIslandProps({
    dashboardFilters,
    isLoading,
    contextDashboardIsUsedIn: 'presentation',
    locale: userLocale,
    i18nLocale,
    updateDashboardFilterValues,
    clearAllSelectedValues,
    requestDashboardFilterSuggestions: requestSuggestions,
    requestDashboardFilterHierarchicalSuggestions:
      requestHierarchicalSuggestions,
    filterCachedHierarchicalSuggestions: filterCachedSuggestions,
    filterPillsUIStateWithSetters,
    dashboardPillLabels: getDashboardPillLabels(i18nLocale),
  });
};

const dashboardSlide$ = combineLatest([
  loadedDashboards$,
  dashboardSlideFetchContext$,
  currentUserPermissionContext$,
  locale$,
  fullscreenWidget$,
  featureContext$,
  i18nLocale$,
  getDashboardFilter$(dashboardSlideFilterSource$),
  dashboardFilterPillsUI$,
  isPptxExportRunning$,
]).pipe(
  map(
    ([
      loadedDashboardsState,
      {
        dashboardId: selectedDashboardId,
        slideId,
        presentationId,
        dashboardParams,
        effectiveFilterValues,
        isFilterChangeOnCurrentSlide,
      },
      permissionContext,
      userLocale,
      { isOpen, widgetData },
      featureContext,
      i18nLocale,
      { dashboardFilters },
      dashboardFilterPillsUIState,
      isExporting,
    ]): DashboardSlideProps => {
      const isPublicPresentation = !permissionContext?.user?._id;
      const trackFilterEvent = isPublicPresentation ? noTracking : track;
      const { apiError } = loadedDashboardsState;
      const isFetchingDashboard =
        loadedDashboardsState.apiLoadingState === LoadingState.LOADING;

      const dashboardData = getSlideDashboardData({
        loadedDashboardData: loadedDashboardsOperations.getLoadedDashboardData(
          loadedDashboardsState,
          selectedDashboardId
        ),
        effectiveFilterValues,
        isFilterChangeOnCurrentSlide,
      });

      const copyUrlButtonProps = getCopyDashboardUrlButtonProps(
        {
          id: selectedDashboardId,
          dashboardFilterValues: effectiveFilterValues,
        },
        DashboardEventLocations.PRESENTATION,
        featureOperations.hasFeature(
          featureContext,
          Features.DISCOVER_CORE_LANDING_PAGE_AND_NAVIGATION
        ),
        i18nLocale
      );

      const dashboardMode =
        dashboardData?._id === METRICS_DSB_ID
          ? DashboardModes.METRICS
          : DashboardModes.VIEW;

      const dashboardColors = getDashboardColors(dashboardData);

      const canViewerFilter = !isSlideFilterLocked(dashboardParams);

      const dashboardFilterIslandProps = canViewerFilter
        ? getSlideFilterIslandProps({
            dashboardId: selectedDashboardId,
            presentationContext:
              presentationId && slideId ? { presentationId, slideId } : null,
            dashboardFilters,
            dashboardFilterPillsUIState,
            isLoading: isFetchingDashboard,
            userLocale,
            i18nLocale,
            trackFilterEvent,
          })
        : { show: false as const };

      const isLoading = !dashboardData && !apiError;

      return {
        capture: {
          slideId,
          isBusy: isLoading || isFetchingDashboard,
          hasError: Boolean(apiError),
          isReport: false,
        },
        dashboardData: dashboardData
          ? {
              ...dashboardData,
              widgets: dashboardData.widgets.map(widget =>
                getWidgetForDashboardSlide(widget, isPublicPresentation)
              ),
            }
          : null,
        mode: dashboardMode,
        userLocale,
        i18nLocale,
        onWidgetDrilldown,
        isLoading,
        apiError,
        trackEvent: trackFilterEvent,
        fullscreenWidgetProps: getFullscreenWidgetModalProps({
          isOpen,
          widgetData,
          dashboardData,
          dashboardColors,
          onClose: closeFullscreenWidget,
          userLocale,
          dashboardMode,
          onWidgetDrilldown,
          i18nLocale,
        }),
        dashboardColors,
        dashboardFilterIslandProps,
        openFullscreenWidget,
        copyUrlButtonProps,
        isCaptureMode: isExporting,
      };
    }
  )
);

export { dashboardSlide$ };
